import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import DashboardPage from "./features/dashboard/DashboardPage";
import Users from "./features/users/users";
import Subscription from "./features/subscription/Subscription";
import Sidebar from "./components/layout/Sidebar";
import Header from "./components/layout/Header";
import "./App.css";
import Settings from "./features/settings/Settings";
import Categories from "./features/categories/Categories";
import Products from "./features/products/Products";
import Industries from "./features/industries/Industries";
import Clients from "./features/clients/Clients";
import Tags from "./features/tags/Tags";
import ApplicationImages from "./features/applicationImages/ApplicationImages";
import Analytics from "./features/analytics/Analytics";
import Login from "./pages/Login";
import ProtectedRoute from "./features/auth/ProtectedRoute";
import ViewClient from "./features/clients/ViewClient";
import ViewIndustry from "./features/industries/ViewIndustry";
import ViewCategory from "./features/categories/ViewCategory";
import ProductDetailsPage from "./features/products/ViewProducts";
import AddProducts from "./features/products/AddProducts";
import ViewUser from "./features/users/ViewUser";
import CaseStudy from "./features/caseStudy/CaseStudy";
import AddCaseStudy from "./features/caseStudy/AddCaseStudy";
import CaseStudyDetails from "./features/caseStudy/ViewCaseStudy";
import SubIndustries from "./features/sub-industries/Sub-Industries";
import AddSubIndustry from "./features/sub-industries/Add-SubIndustry";
import Brand from "./features/brand/Brand";
import ViewApplicationImage from "./features/applicationImages/ViewApplicationImages";
import ActivityLog from "./features/activityLog/ActivityLog";
import Enquiries from "./features/enquiries/enquiries";
import ViewEnquiry from "./features/enquiries/ViewEnquiry";
import Profile from "./features/profile/Profile";
import Plans from "./features/subscription/Plans";

/* =========================
   MAIN LAYOUT
========================= */
const MainLayout = () => {
  const location = useLocation();

  // Sidebar open state
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > 900);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 900);

  /* ---------- TOGGLE ---------- */
  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  /* ---------- CLOSE SIDEBAR (PASSED TO SIDEBAR) ---------- */
  const closeSidebar = () => {
    if (window.innerWidth <= 900) {
      setSidebarOpen(false);
    }
  };

  /* ---------- HANDLE RESIZE ---------- */
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 900;
      setIsMobile(mobile);

      if (mobile) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <ProtectedRoute>
        {/* SIDEBAR */}
        <Sidebar isOpen={sidebarOpen} closeSidebar={closeSidebar} />

        {/* OVERLAY (MOBILE ONLY) */}
        {sidebarOpen && isMobile && (
          <div
            className="sidebar-overlay"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* MAIN CONTENT */}
        <div className={`main-wrapper ${sidebarOpen ? "" : "sidebar-closed"}`}>
          <Header onToggleSidebar={toggleSidebar} />

          <main className="main-content">
            <Routes location={location} key={location.pathname}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/users" element={<Users />} />
              <Route path="/user/view/:id" element={<ViewUser />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/products" element={<Products />} />
              <Route path="/products/addproducts" element={<AddProducts />} />
              <Route path="/products/edit/:id" element={<AddProducts />} />
              <Route path="/industries" element={<Industries />} />
              <Route path="/sub-industries" element={<SubIndustries />} />
              <Route
                path="/sub-industries/add-subIndustry"
                element={<AddSubIndustry />}
              />
              <Route
                path="/sub-industries/edit-subIndustry/:id"
                element={<AddSubIndustry />}
              />
              <Route path="/clients" element={<Clients />} />
              <Route path="/brand" element={<Brand />} />
              <Route path="/tags" element={<Tags />} />
              <Route
                path="/applicationImages"
                element={<ApplicationImages />}
              />
              <Route path="/applicationImages/view/:id" element={<ViewApplicationImage />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/clients/view/:id" element={<ViewClient />} />
              <Route path="/industries/view/:id" element={<ViewIndustry />} />
              <Route path="/categories/view/:id" element={<ViewCategory />} />
              <Route
                path="/products/view/:id"
                element={<ProductDetailsPage />}
              />
              <Route path="/case-studies" element={<CaseStudy />} />
              <Route
                path="/case-studies/add-caseStudy"
                element={<AddCaseStudy />}
              />
              <Route
                path="/case-studies/view/:id"
                element={<CaseStudyDetails />}
              />
              <Route path="/case-studies/edit/:id" element={<AddCaseStudy />} />
              <Route path="/activity-log" element={<ActivityLog />} />
              <Route path="/enquiries" element={<Enquiries />} />
              <Route path="/enquiries/view/:id" element={<ViewEnquiry />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/plans" element={<Plans />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/edit/:id" element={<Settings />} />
            </Routes>
          </main>
        </div>
      </ProtectedRoute>
    </>
  );
};

/* =========================
   APP ROOT
========================= */
export default function App() {
  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: { fontSize: "1rem" },
        }}
      />

      <div className="App">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/*" element={<MainLayout />} />
        </Routes>
      </div>
    </>
  );
}
